import React, { useState } from 'react';
import { dateTimeToMonDDYYYYWithTime } from '../utils/dateUtils';
const ApprovalProgress = ({ progress, requiredSteps, currentUserRole, onApprove, onDeny, renderSupervisorActions, updateHistory, originalWaiverData, minimized = false }) => {
  // Merge required steps with actual progress
  const mergedSteps = React.useMemo(() => {
    const progressMap = new Map(progress.map(p => [p.step, p]));
    
    return requiredSteps.map(step => {
      const progressStep = progressMap.get(step.step);
      return {
        ...step,
        status: progressStep ? progressStep.status : "incomplete",
        _id: progressStep?._id || `step-${step.step}`
      };
    }).sort((a, b) => a.step - b.step);
  }, [progress, requiredSteps]);

  // Check permissions for current user
  const checkDeciderPermissions = (requiredSteps, progress, decider) => {
    let nextStep = null;
    let highestStatus = null;

    // Find the current step for the decider
    const requiredStepForDecider = requiredSteps.find(step => step.decider === decider);
    if (!requiredStepForDecider) return { canModify: false, canDecide: false, decision: null, isHighestRejected: false };

    // Find the progress for this step
    const progressForDeciderStep = progress.find(p => p.step === requiredStepForDecider.step);
    const decision = progressForDeciderStep ? progressForDeciderStep.status : null;

    // Determine the last completed step in progress
    const highestCompletedStep = progress.reduce((highest, step) => {
      return step.step > highest.step ? step : highest;
    }, { step: 0, status: null });

    highestStatus = highestCompletedStep.status;
    nextStep = highestCompletedStep.step + 1;

    // Determine permissions
    const isHighestRejected = highestStatus === "rejected";
    const canDecide = isHighestRejected 
      ? (requiredStepForDecider.step >= highestCompletedStep.step)
      : (requiredStepForDecider.step >= nextStep);
    const canModify = isHighestRejected || requiredStepForDecider.step >= highestCompletedStep.step;

    return {
      canModify,
      canDecide: true, // Always allow decision as per original logic
      decision,
      isHighestRejected
    };
  };

  const myPowers = checkDeciderPermissions(requiredSteps, progress, currentUserRole);

  const getStatusColor = (status) => {
    switch (status) {
      case 'complete': return '#a1f755';
      case 'rejected': return '#ffa5a5';
      case 'incomplete': return '#f0f0f0';
      default: return '#f0f0f0';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'complete': return 'COMPLETE';
      case 'rejected': return 'DENIED';
      case 'incomplete': return 'PENDING';
      default: return 'PENDING';
    }
  };

  const canUserActOnStep = (step) => {
    return step.decider === currentUserRole;
  };

  // Check if there have been updates since the user's last decision
  const checkForUpdatesSinceLastDecision = (userRole) => {
    if (!updateHistory || !originalWaiverData) return null;
    
    // Find the user's last decision in progress
    const userStep = requiredSteps.find(step => step.decider === userRole);
    if (!userStep) return null;
    
    const userProgress = progress.find(p => p.step === userStep.step);
    if (!userProgress || !userProgress.completedAt) return null;
    
    const lastDecisionTime = new Date(userProgress.completedAt);
    
    // Find the most recent update that's newer than the last decision
    const recentUpdates = updateHistory.filter(entry => {
      if (entry.action === "updated" || entry.action === "Request Created") {
        const updateTime = new Date(entry.timestamp);
        return updateTime > lastDecisionTime;
      }
      return false;
    });
    
    if (recentUpdates.length > 0) {
      const mostRecentUpdate = recentUpdates[0];
      return {
        message: "An update was made since your last decision",
        timestamp: mostRecentUpdate.timestamp,
        who: mostRecentUpdate.who
      };
    }
    
    return null;
  };

  // Get status message for current user
  const getCurrentUserStatusMessage = () => {
    const userStep = requiredSteps.find(step => step.decider === currentUserRole);
    if (!userStep) return null;
    
    const userProgress = progress.find(p => p.step === userStep.step);
    const currentDecision = userProgress ? userProgress.status : null;
    
    // Check if user is at a lower level than the highest completed step
    const highestCompletedStep = progress.reduce((highest, step) => {
      return (step.status === 'complete' || step.status === 'rejected') && step.step > highest.step ? step : highest;
    }, { step: 0, status: null });
    
    if (highestCompletedStep.step > userStep.step && highestCompletedStep.status === 'complete') {
      return {
        type: 'disabled',
        message: `Form is disabled - ${requiredSteps.find(s => s.step === highestCompletedStep.step)?.title} has already approved`
      };
    }
    
    if (highestCompletedStep.step > userStep.step && highestCompletedStep.status === 'rejected') {
      return {
        type: 'enabled',
        message: "Form is enabled - you can update since the highest level was denied"
      };
    }
    
    // Check for updates since last decision
    const updateInfo = checkForUpdatesSinceLastDecision(currentUserRole);
    if (updateInfo) {
      return {
        type: 'update',
        message: updateInfo.message,
        details: `Updated by ${updateInfo.who} on ${dateTimeToMonDDYYYYWithTime(updateInfo.timestamp)}`
      };
    }
    
    return null;
  };

  const [expanded, setExpanded] = useState(!minimized);

  if (!expanded) {
    return (
      <div style={{
        position: 'fixed',
        top: 70,
        left: 24,
        zIndex: 2000,
        background: '#283593',
        borderRadius: 12,
        boxShadow: '0 4px 24px rgba(60,72,100,0.18)',
        padding: '8px 18px',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: 15,
        border: '2px solid #e3e8f0',
        minWidth: 44,
        minHeight: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'box-shadow 0.2s',
      }}
        onClick={() => setExpanded(true)}
        title="Show Approval Progress"
      >
        &#x25B6; Approval Progress
      </div>
    );
  }
  return (
    <div style={{
      position: 'fixed',
      top: 70,
      left: 24,
      zIndex: 2000,
      background: '#fff',
      borderRadius: 16,
      padding: 0,
      boxShadow: '0 4px 24px rgba(60,72,100,0.18)',
      border: '2px solid #e3e8f0',
      minWidth: 400,
      maxWidth: 600,
      width: '100%',
      transition: 'box-shadow 0.2s',
      overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#283593', color: 'white', padding: '10px 18px', borderTopLeftRadius: 14, borderTopRightRadius: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 18, letterSpacing: 1 }}>Approval Progress</div>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            fontWeight: 700,
            fontSize: 18,
            cursor: 'pointer',
            marginLeft: 8,
          }}
          title="Minimize"
        >
          &#x2212;
        </button>
      </div>
      <div style={{ padding: 20, paddingTop: 16, background: '#fff', borderBottomLeftRadius: 16, borderBottomRightRadius: 16,paddingBottom: 4}}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {mergedSteps.map((step, index) => {
            const isCurrentUserStep = step.decider === currentUserRole;
            // Show actions if user is the decider for this step and can decide
            const canAct = isCurrentUserStep && myPowers.canDecide;
            return (
              <div key={step._id} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 8,
                border: canUserActOnStep(step) ? '3px solid #001ffe' : '1px solid #e3e8f0',
                backgroundColor: getStatusColor(step.status),
                position: 'relative',
                minHeight: 60
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  gap: 12
                }}>
                  <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: step.status === 'complete' ? '#4caf50' :
                                   step.status === 'rejected' ? '#f44336' : '#ccc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: 14
                  }}>
                    {step.step}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 16, color: '#283593' }}>
                      {step.title}
                    </div>
                    <div style={{ fontSize: 14, color: '#666', marginTop: 2 }}>
                      {step.decider === 'student' ? 'Student' :
                        step.decider === 'supervisor' ? 'Supervisor' :
                        step.decider === 'schoolAdmin' ? 'School Administrator' :
                        step.decider === 'systemAdmin' ? 'System Administrator' : step.decider}
                    </div>
                  </div>
                  <div style={{
                    padding: '4px 12px',
                    borderRadius: 6,
                    backgroundColor: step.status === 'complete' ? '#4caf50' :
                                   step.status === 'rejected' ? '#f44336' : '#ccc',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: 12
                  }}>
                    {getStatusText(step.status)}
                  </div>
                  {/* Admin actions go here - show for all admin roles */}
                  {canAct && renderSupervisorActions && step.decider !== 'student' && (
                    <div style={{ marginLeft: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
                      {renderSupervisorActions()}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ color: '#a40000', fontSize: 14, textAlign: 'center', marginTop: 4, fontStyle: 'italic' }}>
          Approve/Deny will save any changes made.
        </div>
        
        {/* Status message for current user */}
        {(() => {
          const statusMessage = getCurrentUserStatusMessage();
          if (!statusMessage) return null;
          
          const getMessageStyle = () => {
            switch (statusMessage.type) {
              case 'disabled':
                return {
                  background: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  color: '#856404',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  marginTop: '8px',
                  fontSize: '13px',
                  fontWeight: '500'
                };
              case 'enabled':
                return {
                  background: '#d4edda',
                  border: '1px solid #c3e6cb',
                  color: '#155724',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  marginTop: '8px',
                  fontSize: '13px',
                  fontWeight: '500'
                };
              case 'update':
                return {
                  background: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  color: '#721c24',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  marginTop: '8px',
                  fontSize: '13px',
                  fontWeight: '500'
                };
              default:
                return {};
            }
          };
          
          return (
            <div style={getMessageStyle()}>
              <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                {statusMessage.message}
              </div>
              {statusMessage.details && (
                <div style={{ fontSize: '12px', opacity: '0.8' }}>
                  {statusMessage.details}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ApprovalProgress; 